import {
	MeditationRetreatStatus,
	MeditatorPassFailActions,
	MeditatorStatus,
	TableTitles,
} from "@/constants";
import { useDisclosure } from "@/hooks";
import { useGetMeditationRetreatDetail } from "@/hooks/query-hooks/useMeditationRetreats";
import {
	useGetMeditatorsByMeditationRetreat,
	usePassAndFailMeditatorsToRetreat,
} from "@/hooks/query-hooks/useMeditators";
import { cn } from "@/libs/cn";
import { CLIENT_API } from "@/services/axios-client";
import { useNotificationStore } from "@/states/zustand/notification";
import {
	Breadcrumb,
	Button,
	Divider,
	Drawer,
	Flex,
	Form,
	Modal,
	Space,
	Upload,
	UploadFile,
} from "antd";
import { TableRowSelection } from "antd/es/table/interface";
import { UploadChangeParam } from "antd/es/upload";
import dayjs from "dayjs";
import download from "downloadjs";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
	AiOutlineExport,
	AiOutlineEye,
	AiOutlineLeft,
	AiOutlineVideoCamera,
} from "react-icons/ai";
import { Link, useParams } from "react-router";
import { AnimatedComponent } from "../common";
import {
	getMeditationRetreatsStatus,
	MeditationRetreatsTable,
} from "../MeditationRetreats";
import { RegistrantInformations } from "./RegistrantInformations";
import { RegistrantTable } from "./RegistrantTable";

export function MeditatorsByRetreat() {
	const { retreatId } = useParams<{ retreatId: string }>();
	const [selectedRows, setSelectedRows] = useState<number[]>([]);
	const [selectedMeditators, setSelectedMeditators] = useState<
		MeditatorWithMeditationRetreat[]
	>([]);

	const { data: meditationRetreat } = useGetMeditationRetreatDetail(
		retreatId!,
	);

	const { data, isLoading } = useGetMeditatorsByMeditationRetreat(
		retreatId!,
		1,
		"ACCEPTED",
		meditationRetreat?.maxYogiLimit,
	);

	const meditators = useMemo(() => {
		return data?.content || [];
	}, [data, retreatId]);

	const renderColumnsKey = [
		TableTitles.registrant_book_id,
		TableTitles.registrant_name,
		TableTitles.registrant_phone,
		TableTitles.registrant_address,
		TableTitles.registrant_status,
		TableTitles.action,
	];

	const renderRetreatColumnsKey = [
		TableTitles.retreat_start_month,
		TableTitles.retreat_name,
		TableTitles.retreat_level,
		TableTitles.retreat_start_date,
		TableTitles.retreat_end_date,
		TableTitles.total_registrants,
		TableTitles.retreat_status,
	];

	const handleExportExcel = async () => {
		try {
			await CLIENT_API.exportMeditatorsByRetreat(Number(retreatId!)).then(
				(res) => {
					const fileName = `Meditators_In_${meditationRetreat?.level.name}_${meditationRetreat?.title}.xlsx`;

					download(
						res,
						fileName,
						"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
					);
				},
			);
		} catch (error) {
			console.error("Error exporting meditators:", error);
		}
	};

	const rowSelection: TableRowSelection<MeditatorWithMeditationRetreat> = {
		type: "checkbox",
		selectedRowKeys: selectedRows,
		onChange(selectedRowKeys, selectedRows) {
			setSelectedRows(selectedRowKeys as number[]);
			setSelectedMeditators(
				selectedRows as MeditatorWithMeditationRetreat[],
			);
		},
		getCheckboxProps: (record) => {
			const isSelectable =
				record.user?.status === MeditatorStatus.ACCEPTED;

			const isDatesInBetween =
				dayjs(meditationRetreat?.toDate).diff(dayjs(), "day") <= 3 ||
				dayjs(meditationRetreat?.toDate).diff(dayjs(), "day") >= -3;

			return {
				disabled:
					!isSelectable ||
					getMeditationRetreatsStatus(meditationRetreat!) ===
						MeditationRetreatStatus.UPCOMING ||
					!isDatesInBetween,
				title: isSelectable ? "" : "This meditator cannot be selected",
			};
		},
	};

	return (
		<AnimatedComponent className="space-y-6">
			<Helmet>
				<title>
					{meditationRetreat?.title ?? "တရားစခန်းအလိုက်ယောဂီစာရင်း"}
				</title>
			</Helmet>
			<div className="flex items-center justify-between">
				<Breadcrumb
					separator="/"
					items={[
						{
							title: (
								<Link
									to={"/meditator-list-by-retreat"}
									className="text-base font-semibold hover:!bg-transparent"
								>
									တရားစခန်းများ
								</Link>
							),
							key: "retreats",
						},
						{
							title: (
								<p className="text-base font-semibold">
									{meditationRetreat?.title || "Loading..."}
								</p>
							),
							key: "retreat-detail",
						},
					]}
				/>
				<Button
					size="large"
					className={cn("!font-poppins !ml-auto", {
						"bg-primary-gradient !text-white": meditators?.length,
					})}
					icon={<AiOutlineExport />}
					onClick={handleExportExcel}
					disabled={!meditators?.length}
				>
					Export
				</Button>
			</div>
			<Divider />

			<MeditationRetreatsTable
				dataSource={[meditationRetreat!]}
				renderColumnKeys={renderRetreatColumnsKey}
				loading={isLoading}
				pagination={false}
				hasFilter={false}
				rowKey={(record) => record.id}
			/>
			<Divider />

			{selectedRows.length > 0 && (
				<Flex
					justify="space-between"
					align="center"
					className={"!mb-4"}
				>
					<Space>
						<PassAndFailBulkData
							meditators={selectedMeditators}
							action={MeditatorPassFailActions.PASS}
							onComplete={() => setSelectedRows([])}
						/>
						<PassAndFailBulkData
							meditators={selectedMeditators}
							action={MeditatorPassFailActions.FAIL}
							onComplete={() => setSelectedRows([])}
						/>
					</Space>

					<SelectedMeditators meditators={selectedMeditators} />
				</Flex>
			)}

			<RegistrantTable
				dataSource={meditators}
				renderColumnKeys={renderColumnsKey}
				loading={isLoading}
				pagination={{
					pageSize: 10,
				}}
				TableActionComponent={TableActionComponent}
				tableActionProps={{ meditationRetreat }}
				hasFilter={{
					search: true,
				}}
				rowKey={(record) => record.user.id}
				size="small"
				rowSelection={rowSelection}
				// rowSelection={getMeditationRetreatsStatus(meditationRetreat!) === MeditationRetreatStatus.ENDED ? rowSelection : undefined}
			/>
		</AnimatedComponent>
	);
}

const TableActionComponent = ({
	record,
	meditationRetreat,
}: {
	record: MeditatorWithMeditationRetreat;
	meditationRetreat?: MeditationRetreat; // adjust to actual type if needed
}) => {
	const [opened, { toggle }] = useDisclosure(false);

	const showPassFailActions =
		record?.user?.status !== MeditatorStatus.PASSED &&
		meditationRetreat?.status !== MeditationRetreatStatus.UPCOMING;

	return (
		<Space>
			<Button type="link" onClick={toggle}>
				<AiOutlineEye size={16} />
			</Button>
			<Drawer
				open={opened}
				onClose={toggle}
				closeIcon={false}
				destroyOnClose
				width={"79vw"}
				classNames={{
					content: "!w-[cal(100% - 320px)]",
				}}
				title={
					<div>
						<Button
							shape="round"
							onClick={toggle}
							type="text"
							size="large"
							icon={<AiOutlineLeft />}
							className="!font-poppins"
						>
							Back
						</Button>
					</div>
				}
				footer={
					showPassFailActions && (
						<Space className="w-full justify-end">
							<PassAndFailConfirmationModal
								action={MeditatorPassFailActions.FAIL}
								meditator={record.user}
								onComplete={toggle}
							/>
							<PassAndFailConfirmationModal
								action={MeditatorPassFailActions.PASS}
								meditator={record.user}
								onComplete={toggle}
							/>
						</Space>
					)
				}
			>
				<RegistrantInformations registrant={record.user} />
			</Drawer>
		</Space>
	);
};

const PassAndFailConfirmationModal = ({
	action,
	meditator,
	onComplete,
}: {
	action: MeditatorPassFailActions;
	meditator: Meditator;
	onComplete: () => void;
}) => {
	const { retreatId } = useParams<{ retreatId: string }>();
	const [opened, { toggle }] = useDisclosure(false);
	const [videoUrl, setVideoUrl] = useState<string | null>(null);

	const [form] = Form.useForm();

	const { mutate, isMutating } = usePassAndFailMeditatorsToRetreat();

	const handleConfirm = (values: {
		review_video: UploadChangeParam<UploadFile>;
	}) => {
		if (!retreatId) {
			useNotificationStore.getState().setNotification({
				type: "warning",
				message: "No meditation retreat found!",
			});
			return;
		}

		const formData = new FormData();
		formData.append(
			"data",
			new Blob([JSON.stringify({ ids: [meditator.id] })], {
				type: "application/json",
			}),
		);
		formData.append("video", values.review_video.file as unknown as File);

		const payload = {
			retreatId: Number(retreatId),
			data: formData,
			action,
		};

		mutate(payload, {
			onSuccess: () => {
				toggle();
				onComplete();
				form.resetFields();
				setVideoUrl(null);
			},
		});
	};

	return (
		<>
			<Button
				size="large"
				shape="round"
				type="primary"
				onClick={toggle}
				color="green"
				className={cn("!font-poppins ", {
					"!bg-success": action === MeditatorPassFailActions.PASS,
				})}
				danger={action === MeditatorPassFailActions.FAIL}
			>
				{action === MeditatorPassFailActions.PASS ? "Pass" : "Fail"}
			</Button>
			<Modal
				open={opened}
				onCancel={() => {
					toggle();
					setVideoUrl(null);
					form.resetFields();
				}}
				title={`${action === MeditatorPassFailActions.PASS ? "Pass" : "Fail"} ${meditator?.name}?`}
				okText="Confirm"
				cancelText="Cancel"
				okButtonProps={{
					danger: action === MeditatorPassFailActions.FAIL,
					loading: isMutating,
					className: cn("!font-poppins", {
						"!bg-success": action === MeditatorPassFailActions.PASS,
					}),
					type: "primary",
					size: "large",
					form: `pass-fail-modal-${action}-${meditator.id}`,
					htmlType: "submit",
				}}
				cancelButtonProps={{
					className: "!font-poppins",
					disabled: isMutating,
					type: "text",
					size: "large",
				}}
				closable={!isMutating}
				confirmLoading={isMutating}
				classNames={{
					header: "[&>.ant-modal-title]:!text-lg [&>.ant-modal-title]:!font-semibold [&>.ant-modal-title]:!font-poppins",
				}}
				width={520}
				centered
			>
				<Form
					form={form}
					name={`pass-fail-modal-${action}-${meditator.id}`}
					onFinish={handleConfirm}
					layout="vertical"
				>
					<p className="font-poppins text-sm text-gray-700">
						Please confirm if you want to set the retreat status{" "}
						<strong
							className={
								action === MeditatorPassFailActions.PASS
									? "text-success"
									: "text-danger"
							}
						>
							{action === MeditatorPassFailActions.PASS
								? "pass"
								: "fail"}
						</strong>{" "}
						for{" "}
						<span className="font-semibold">{meditator.name}</span>.
					</p>

					<Divider className="my-4" />

					<p className="font-poppins text-sm text-gray-500 mb-4">
						You may also upload an{" "}
						<span className="font-semibold">optional</span> video to
						provide feedback on their progress.
					</p>
					<Form.Item
						name="review_video"
						label="Video (Optional)"
						className="w-full!"
					>
						<Upload
							accept="video/*"
							maxCount={1}
							beforeUpload={(file) => {
								if (file.size > 400 * 1024 * 1024) {
									useNotificationStore
										.getState()
										.setNotification({
											type: "warning",
											message:
												"Please choose a video smaller than 100MBs.",
										});
									return Upload.LIST_IGNORE;
								}
								const url = URL.createObjectURL(file);
								setVideoUrl(url);
								return false; // Prevent automatic upload
							}}
							onRemove={() => setVideoUrl(null)}
							rootClassName="[&>.ant-upload-select]:w-full!"
						>
							{videoUrl ? (
								<div className="overflow-hidden rounded-xl shadow-sm border border-gray-200">
									<video
										src={videoUrl}
										controls
										className="w-full h-auto aspect-video object-cover cursor-auto"
										onClick={(e) => {
											e.stopPropagation();
										}}
									/>
								</div>
							) : (
								<article className="space-y-1 aspect-video flex flex-col items-center justify-center w-full border border-dashed rounded-xl shadow-sm bg-gray-50 cursor-pointer hover:border-blue-500 transition-colors ">
									<div className=" size-12 bg-primary-gradient flex items-center justify-center rounded-full">
										<AiOutlineVideoCamera
											size={20}
											className="text-white"
										/>
									</div>
									<h3 className="font-poppins">
										Tap to upload a video
									</h3>
									<p className="text-gray-400 font-poppins">
										MP4, MOV, AVI · Max 100MB
									</p>
								</article>
							)}
						</Upload>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

const SelectedMeditators = ({
	meditators,
}: {
	meditators: MeditatorWithMeditationRetreat[];
}) => {
	const [opened, { toggle }] = useDisclosure(false);

	const renderColumnsKey = [
		TableTitles.No,
		TableTitles.registrant_book_id,
		TableTitles.registrant_name,
		TableTitles.registrant_phone,
	];

	return (
		<>
			<Button
				size="large"
				className="!font-poppins rounded-lg!"
				icon={<AiOutlineEye size={16} />}
				onClick={toggle}
			>
				({meditators.length}) Selected
			</Button>
			<Modal
				open={opened}
				onCancel={toggle}
				title="Selected Meditators"
				footer={null}
				classNames={{
					header: "[&>.ant-modal-title]:!text-lg [&>.ant-modal-title]:!font-semibold [&>.ant-modal-title]:!font-poppins",
				}}
				width={800}
				// centered
			>
				<RegistrantTable
					dataSource={meditators}
					renderColumnKeys={renderColumnsKey}
					loading={false}
					rowKey={(record) => record.user.id}
					pagination={false}
					scroll={{ y: 500 }}
				/>
			</Modal>
		</>
	);
};

const PassAndFailBulkData = ({
	action,
	meditators,
	onComplete,
}: {
	action: MeditatorPassFailActions;
	meditators: MeditatorWithMeditationRetreat[];
	onComplete?: () => void;
}) => {
	const [modal, contextHolder] = Modal.useModal();

	const { retreatId } = useParams<{ retreatId: string }>();
	const { mutate } = usePassAndFailMeditatorsToRetreat();

	const onConfirm = () => {
		const formData = new FormData();
		formData.append(
			"data",
			new Blob(
				[JSON.stringify({ ids: meditators?.map((it) => it.user.id) })],
				{
					type: "application/json",
				},
			),
		);

		modal.confirm({
			title: (
				<h2 className="font-poppins">
					{action === MeditatorPassFailActions.PASS ? "Pass" : "Fail"}{" "}
					({meditators.length}) selected Meditators
				</h2>
			),
			content: (
				<p className="font-poppins text-sm">
					Are you sure you want to{" "}
					{action === MeditatorPassFailActions.PASS ? "pass" : "fail"}{" "}
					this ({meditators.length}) selected meditators?
				</p>
			),
			okButtonProps: {
				danger: action === MeditatorPassFailActions.FAIL,
				className: cn("!font-poppins", {
					"!bg-success": action === MeditatorPassFailActions.PASS,
				}),
				type: "primary",
				size: "large",
			},
			cancelButtonProps: {
				className: "!font-poppins",
				type: "text",
				size: "large",
			},
			onOk: () => {
				return new Promise((resolve, reject) => {
					mutate(
						{
							retreatId: Number(retreatId),
							data: formData,
							action,
						},
						{
							onSuccess: () =>
								resolve(onComplete && onComplete()),
							onError: reject,
						},
					);
				});
			},
		});
	};

	return (
		<>
			{contextHolder}
			<Button
				size="large"
				type="primary"
				onClick={onConfirm}
				className={cn("!font-poppins rounded-lg! ", {
					"!bg-success": action === MeditatorPassFailActions.PASS,
				})}
				danger={action === MeditatorPassFailActions.FAIL}
			>
				{action === MeditatorPassFailActions.PASS ? "Pass" : "Fail"}
			</Button>
		</>
	);
};
